<template>
  <o-dialog
    ref="dialog"
    :title="title"
    :info="info"
    :options="[
      { text: $t('general.back'), type: 'cancel' },
      { text: $t('general.send'), type: 'confirm', callback: confirm },
    ]"
    :portal="portal"
  />
</template>

<script setup lang="ts">
import { ReservationEmailType, ReservationEmailTypeLabels } from '~/_api/models/reservation'

import type { Reservation } from '~/_api/models/reservation'

interface Props {
  reservation: Reservation
  portal?: string | null
  loading?: boolean
}

interface Emits {
  (e: 'update:loading', value: boolean): void
}

const props = withDefaults(defineProps<Props>(), {
  portal: null,
  loading: false,
})

const emit = defineEmits<Emits>()
const { $api, $i18n, $notification } = useNuxtApp()

const dialog = ref<any>()
const type = ref<ReservationEmailType | null>(null)

const title = computed(() => {
  if (!type.value) return ''
  const label = ReservationEmailTypeLabels[type.value]
  return label ? $i18n.t(label) : ''
})

const recipient = computed(() => {
  return (props.reservation as any).isGroup ? 'all attendees' : (props.reservation as any).customer?.displayName || 'customer'
})

const info = computed(() => {
  switch (type.value) {
    case ReservationEmailType.received:
      return $i18n.t('components.reservation.actions.sendEmail.received', { recipient: recipient.value })

    case ReservationEmailType.confirmation:
      return $i18n.t('components.reservation.actions.sendEmail.confirmation', { recipient: recipient.value })

    case ReservationEmailType.reminderEmail:
      return $i18n.t('components.reservation.actions.sendEmail.reminderEmail', { recipient: recipient.value })

    case ReservationEmailType.reminderSMS:
      return $i18n.t('components.reservation.actions.sendEmail.reminderSMS', { recipient: recipient.value })

    case ReservationEmailType.reviewRequest:
      return $i18n.t('components.reservation.actions.sendEmail.reviewRequest', { recipient: recipient.value })

    default:
      return $i18n.t('components.reservation.actions.sendEmail.default', { recipient: recipient.value })
  }
})

const open = (emailType: ReservationEmailType) => {
  type.value = emailType
  dialog.value.open()
}

const confirm = async () => {
  if (!type.value) return

  emit('update:loading', true)

  try {
    const reservation = props.reservation as any
    const date = reservation.date?.integer ?? reservation.date
    const isAttendee = reservation.idReservationCustomer != null
    const reservationId = reservation.objectID?.() ?? reservation.idReservation?.toString()
    const reservationCustomerId = reservation.objectID?.() ?? reservation.idReservationCustomer?.toString()
    const response = isAttendee
      ? await $api.reservationCustomer.email(reservationCustomerId, date, type.value)
      : await $api.reservation.email(reservationId, date, type.value)

    if (response) {
      emit('update:loading', false)

      const typeLabel = type.value ? ReservationEmailTypeLabels[type.value] : ''
      $notification.toast({
        title: $i18n.t('general.messageSent'),
        text: $i18n.t('components.reservation.actions.sendEmail.successInfo', { description: typeLabel ? $i18n.t(typeLabel) : '', recipient: recipient.value }),
        variant: 'success',
      })

      return true
    }
  } catch (e: any) {
    console.log(e)
    $notification.error({ error: e?.errors ?? e?.exception ?? e })
  }

  emit('update:loading', false)
}

defineExpose({
  open,
})
</script>
